import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from 'contexts/ApiContext';

import useRequiredParams from '@polyai/common/hooks/useRequiredParams';

import { HTTPError } from 'api/error';
import type { ApiConfigResponse } from 'api/resources/apiConfig/types';
import { useGetApiConfigQueryKey } from 'hooks/api/apiConfig/hooks';
import { useToast } from 'hooks/useToast';

interface UseApiConfigDeleteOptions {
  options?: Parameters<typeof useMutation<void, Error, string>>[1];
}

export const useApiConfigDelete = ({
  options,
}: UseApiConfigDeleteOptions = {}) => {
  const api = useApi();
  const { accountId } = useRequiredParams<{ accountId: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();

  const queryKey = useGetApiConfigQueryKey(accountId);

  return useMutation<void, Error, string>(
    projectId => api.methods.deleteApiConfig(accountId, projectId),
    {
      retry: false,
      onSuccess: (_data, projectId) => {
        queryClient.setQueryData<ApiConfigResponse>(queryKey, prev => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            configs: prev.configs.filter(c => c.project_id !== projectId),
          };
        });
        toast.success({ title: 'API config cleared' });
      },

      onError: error => {
        const isNotFound =
          error instanceof HTTPError && error.httpStatus === 404;

        if (isNotFound) {
          toast.info({ title: 'Nothing to clear' });
          return;
        }

        toast.error({
          title: 'There was an error clearing the API config',
        });
      },
      ...options,
    },
  );
};

export default useApiConfigDelete;
